import * as React from "react";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { translate } from "../../../../../../translate/translate";
import { Holiday } from "../../../../../../services/npi-holiday-service";
import { MONTH_LABELS } from "./calendar-utils";

interface NpiHolidayMonthListProps {
    month?: number;
    title?: string;
    holidays: Holiday[];
    onDelete: (holiday: Holiday) => void;
}

const getHolidayStyle = (holiday: Holiday) => {
    if (holiday.country === "CHINA") {
        if (holiday.holiday_type === "tiaoxiu") {
            return {
                borderColor: "#F0A93C", backgroundColor: "#FEF6E8",
                badgeColor: "#F0A93C", badgeLabel: "Tiaoxiu",
            };
        }
        return {
            borderColor: "#F3C3C3", backgroundColor: "#FDF4F4",
            badgeColor: "#E85D5D", badgeLabel: "Feriado",
        };
    }
    if (holiday.scope === "MANAUS") {
        return {
            borderColor: "#3FBF9F", backgroundColor: "#E9FBF6",
            badgeColor: "#3FBF9F", badgeLabel: "Feriado",
        };
    }
    if (holiday.scope === "SAO_PAULO") {
        return {
            borderColor: "#4D8AF0", backgroundColor: "#EAF2FE",
            badgeColor: "#4D8AF0", badgeLabel: "Feriado",
        };
    }
    return {
        borderColor: "#F3C3C3", backgroundColor: "#FDF4F4",
        badgeColor: "#E85D5D", badgeLabel: "Feriado",
    };
};

const regionLabel = (holiday: Holiday) => {
    if (holiday.country === "CHINA") {
        return "CHINA";
    }
    if (holiday.scope === "MANAUS") {
        return "Manaus";
    }
    if (holiday.scope === "SAO_PAULO") {
        return "São Paulo";
    }
    return "Brasil";
};

const NpiHolidayMonthListComponent: React.FC<NpiHolidayMonthListProps> = ({ month, title, holidays, onDelete }) => {
    const sorted = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

    return (
        <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>
                {title ?? `${translate.t("holidays_and_events_of_month")} (${MONTH_LABELS[month ?? 0]})`}
            </Typography>
            <Stack spacing={1}>
                {sorted.map((holiday) => {
                    const style = getHolidayStyle(holiday);
                    return (
                        <Box
                            key={holiday.id}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                border: `1px solid ${style.borderColor}`,
                                backgroundColor: style.backgroundColor,
                                borderRadius: "10px",
                                px: 2,
                                py: 1.5,
                            }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {holiday.country === "CHINA" ? "🇨🇳" : "🇧🇷"} {holiday.description}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate.t("date")}: <b>{holiday.date}</b> | {regionLabel(holiday)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                                {holiday.source === "manual" && (
                                    <IconButton size="small" onClick={() => onDelete(holiday)}>
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                )}
                                <Chip label={style.badgeLabel} sx={{ backgroundColor: style.badgeColor, color: "#FFFFFF", fontWeight: 700 }} />
                            </Box>
                        </Box>
                    );
                })}
                {sorted.length === 0 && (
                    <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                        {translate.t("no_holidays_this_month")}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export default NpiHolidayMonthListComponent;